import Vue from "vue";
import Vuex from "vuex";
import { State } from "./state";
import { SessionStorageAccessor } from "./SessionStorageAccessor";
import { WebStorageAccessor } from "./WebStorageAccessor";
import { IDrawableItem } from "../domain/document/items";
import { IPdfViewerVisibleControl } from "../domain/system";
import { IFixedPhraseTextDragParameter, IDocumentDataDragParameter } from "../components/dragging";

Vue.use(Vuex);

const store = new Vuex.Store({
    state: new State(),
    getters: {
      token: state => state.token,
      user: state => state.user,
      department: state => state.department,
      isAuthorized: state => state.token && (state.token !== ""),
      configuration: state => state.configuration,
      extraConfiguration: state => state.extraConfiguration,
      userAuthority: state => state.userAuthority,
      hostName: state => state.hostName,
      userDepartments: state => state.userDepartments,
      orderUser: state => state.orderUser,
      attendingUser: state => state.attendingUser,
      clientIpAddress: state => state.clientIpAddress,
      departmentGroup: state => state.departmentGroup,
      referenceableDepartmentGroups: state => state.referenceableDepartmentGroups,
      isManualCloseable: state => state.isManualCloseable,
      isDepartmentGroupScopeEnable: state => state.isDepartmentGroupScopeEnable,
      userSelection: state => state.userSelection,
    },
    mutations: {
      setToken(state, token) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "token", token);
        SessionStorageAccessor.token = token;
      },
      setUser(state, user) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "user", user);
        SessionStorageAccessor.user = user;
      },
      setDepartment(state, department) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "department", department);
        SessionStorageAccessor.department = department;
      },
      setConfiguration(state, configuration) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "configuration", configuration);
        SessionStorageAccessor.configuration = configuration;
      },
      setExtraConfiguration(state, extraConfiguration) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "extraConfiguration", extraConfiguration);
        SessionStorageAccessor.extraConfiguration = extraConfiguration;
      },
      setUserAuthority(state, userAuthority) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "userAuthority", userAuthority);
        SessionStorageAccessor.userAuthority = userAuthority;
      },
      setHostName(state, hostName) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "hostName", hostName);
        WebStorageAccessor.hostName = hostName;
      },
      setPdfViewerVisibleControlObject(state, payload: IPdfViewerVisibleControl) {
        // プロパティとしてアクセスされるゲッターは Vue のリアクティブシステムの一部としてキャッシュされるため未定義
        // 非同期的に処理する必要が無いためアクションも未定義
        // モジュールに移すことも検討する
        // PDF表示状態を管理するために例外的に使用(IE対策)
        // PDF表示部が重なるダイアログ表示中に使用する
        if (payload.isDelete) {
          state.pdfViewerVisibleControlObjects = state.pdfViewerVisibleControlObjects.filter((v) => v.symbol !== payload.symbol);
        } else {
          const index = state.pdfViewerVisibleControlObjects
            .map((v, i) => ({ value: v, index: i }))
            .filter((v) => v.value.symbol === payload.symbol)
            .map(v => v.index);
          if (index.length === 0) {
            state.pdfViewerVisibleControlObjects.push(payload);
          } else {
            state.pdfViewerVisibleControlObjects.splice(index[0], 1, payload);
          }
        }
      },
      setFixedPhraseTextDragParameter(state: State, dragParameter: IFixedPhraseTextDragParameter) {
        // プロパティとしてアクセスされるゲッターは Vue のリアクティブシステムの一部としてキャッシュされるため未定義
        // 非同期的に処理する必要が無いためアクションも未定義
        // 定型文のD&D時にパラメータを渡すために使用する(JSONで受け渡した場合、展開できないパターンがあるため)
        state.fixedPhraseTextDragParameter = dragParameter;
      },
      setDocumentDataDragParameter(state: State, dragParameter: IDocumentDataDragParameter) {
        // プロパティとしてアクセスされるゲッターは Vue のリアクティブシステムの一部としてキャッシュされるため未定義
        // 非同期的に処理する必要が無いためアクションも未定義
        // 過去データの値をD&D時にパラメータを渡すために使用する(JSONで受け渡した場合、展開できないパターンがあるため)
        state.documentDataDragParameter = dragParameter;
      },
      setUserDepartments(state, userDepartments) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "userDepartments", userDepartments);
        SessionStorageAccessor.userDepartments = userDepartments;
      },
      setOrderUser(state, orderUser) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "orderUser", orderUser);
        SessionStorageAccessor.orderUser = orderUser;
      },
      setAttendingUser(state, attendingUser) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "attendingUser", attendingUser);
        SessionStorageAccessor.attendingUser = attendingUser;
      },
      setClientIpAddress(state, clientIpAddress) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "clientIpAddress", clientIpAddress);
        SessionStorageAccessor.clientIpAddress = clientIpAddress;
      },
      setObjectClipboard(state: State, objectClipboard: IDrawableItem) {
        // 非同期的に処理する必要が無いためアクションは未定義
        // オブジェクトのコピー＆ペーストに使用する
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "objectClipboard", Object.freeze(objectClipboard.clone()));
      },
      setDepartmentGroup(state, departmentGroup) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "departmentGroup", departmentGroup);
        SessionStorageAccessor.departmentGroup = departmentGroup;
      },
      setReferenceableDepartmentGroups(state, departmentGroups) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "referenceableDepartmentGroups", departmentGroups);
        SessionStorageAccessor.referenceableDepartmentGroups = departmentGroups;
      },
      setIsManualCloseable(state, isManualCloseable) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "isManualCloseable", isManualCloseable);
        SessionStorageAccessor.isManualCloseable = isManualCloseable;
      },
      setIsDepartmentGroupScopeEnable(state, isDepartmentGroupScopeEnable) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "isDepartmentGroupScopeEnable", isDepartmentGroupScopeEnable);
        SessionStorageAccessor.isDepartmentGroupScopeEnable = isDepartmentGroupScopeEnable;
      },
      setUserSelection(state, userSelection) {
        // stateに直接入れると何故かgetterによる更新がされない
        Vue.set(state, "userSelection", userSelection);
        SessionStorageAccessor.userSelection = userSelection;
      },
    },
    actions: {
      setToken(context, token) {
        context.commit("setToken", token);
      },
      setUser(context, user) {
        context.commit("setUser", user);
      },
      setDepartment(context, department) {
        context.commit("setDepartment", department);
      },
      setConfiguration(context, configuration) {
        context.commit("setConfiguration", configuration);
      },
      setExtraConfiguration(context, extraConfiguration) {
        context.commit("setExtraConfiguration", extraConfiguration);
      },
      setUserAuthority(context, userAuthority) {
        context.commit("setUserAuthority", userAuthority);
      },
      setHostName(context, hostName) {
        context.commit("setHostName", hostName);
      },
      setUserDepartments(context, userDepartments) {
        context.commit("setUserDepartments", userDepartments);
      },
      setOrderUser(context, orderUser) {
        context.commit("setOrderUser", orderUser);
      },
      setAttendingUser(context, attendingUser) {
        context.commit("setAttendingUser", attendingUser);
      },
      setClientIpAddress(context, clientIpAddress) {
        context.commit("setClientIpAddress", clientIpAddress);
      },
      setDepartmentGroup(context, departmentGroup) {
        context.commit("setDepartmentGroup", departmentGroup);
      },
      setReferenceableDepartmentGroups(context, departmentGroups) {
        context.commit("setReferenceableDepartmentGroups", departmentGroups);
      },
      setIsManualCloseable(context, isManualCloseable) {
        context.commit("setIsManualCloseable", isManualCloseable);
      },
      setIsDepartmentGroupScopeEnable(context, isDepartmentGroupScopeEnable) {
        context.commit("setIsDepartmentGroupScopeEnable", isDepartmentGroupScopeEnable);
      },
      setUserSelection(context, userSelection) {
        context.commit("setUserSelection", userSelection);
      },
    }
  }
);

export default store;
